import React from 'react';
import { StyleSheet, View } from 'react-native';

import { AnimatedViews, HStack, ProductCardV2, theme } from '@sg/garnish';

// ─────────────────────────────────────────────────────────────────────────────

/**
 * Favorites placeholders shaped like the favorite cards, laid out in the same
 * grid, so the page keeps its shape when the favorites arrive.
 */
export const AccountFavoritesLoading = (props: Props) => {
  const { itemsPerRow } = props;

  const placeholderIndexes = [...Array(itemsPerRow * PLACEHOLDER_ROWS).keys()];

  return (
    <HStack itemsPerRow={itemsPerRow} style={styles.stack}>
      {placeholderIndexes.map((index) => (
        <FavoriteCardPlaceholder key={index} index={index} />
      ))}
    </HStack>
  );
};

// ─── Components ──────────────────────────────────────────────────────────────

const FavoriteCardPlaceholder = (props: FavoriteCardPlaceholderProps) => {
  const { index } = props;

  return (
    <ProductCardV2.Container canHover={false}>
      <AnimatedViews.Pulse index={index} style={styles.content}>
        <View style={styles.imageArea}>
          <View style={styles.image} />
        </View>

        <View style={styles.labelRow}>
          <View style={styles.label} />
        </View>

        <View style={styles.heading} />

        <View style={styles.description}>
          <View style={styles.descriptionLineFull} />

          <View style={styles.descriptionLineLong} />

          <View style={styles.descriptionLineShort} />
        </View>

        <View style={styles.footer}>
          <View style={styles.calories} />

          <View style={styles.buttons}>
            <View style={styles.addToBagButton} />

            <View style={styles.shareIcon} />
          </View>
        </View>
      </AnimatedViews.Pulse>
    </ProductCardV2.Container>
  );
};

// ─── Constants ───────────────────────────────────────────────────────────────

const PLACEHOLDER_ROWS = 2;

// ─── Styles ──────────────────────────────────────────────────────────────────

const styles = StyleSheet.create({
  stack: {
    marginTop: theme.spacing['4'],
  },
  content: {
    flexGrow: 1,
    rowGap: theme.spacing['1'],
  },
  imageArea: {
    height: 343,
    alignItems: 'center',
    justifyContent: 'center',
  },
  image: {
    width: 280,
    height: 280,
    borderRadius: theme.radius.xlarge,
    backgroundColor: theme.colors.OPACITY.DARK_KALE.LIGHTEST,
  },
  labelRow: {
    minHeight: 22,
    justifyContent: 'flex-end',
  },
  label: {
    width: 88,
    height: 12,
    marginBottom: 3,
    borderRadius: theme.radius.small,
    backgroundColor: theme.colors.OPACITY.DARK_KALE.LIGHTEST,
  },
  heading: {
    width: '64%',
    height: 20,
    marginVertical: 4,
    borderRadius: theme.radius.small,
    backgroundColor: theme.colors.OPACITY.DARK_KALE.LIGHTEST,
  },
  description: {
    marginBottom: theme.spacing['1'],
  },
  descriptionLineFull: {
    width: '100%',
    height: 14,
    marginVertical: 4,
    borderRadius: theme.radius.small,
    backgroundColor: theme.colors.OPACITY.DARK_KALE.LIGHTEST,
  },
  descriptionLineLong: {
    width: '88%',
    height: 14,
    marginVertical: 4,
    borderRadius: theme.radius.small,
    backgroundColor: theme.colors.OPACITY.DARK_KALE.LIGHTEST,
  },
  descriptionLineShort: {
    width: '52%',
    height: 14,
    marginTop: theme.spacing['3'],
    marginBottom: 4,
    borderRadius: theme.radius.small,
    backgroundColor: theme.colors.OPACITY.DARK_KALE.LIGHTEST,
  },
  footer: {
    marginTop: 'auto',
  },
  calories: {
    width: 64,
    height: 12,
    marginTop: 23,
    marginBottom: 3,
    borderRadius: theme.radius.small,
    backgroundColor: theme.colors.OPACITY.DARK_KALE.LIGHTEST,
  },
  buttons: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginTop: theme.spacing['9'],
  },
  addToBagButton: {
    width: 127,
    height: 48,
    borderRadius: theme.radius['3xLarge'],
    backgroundColor: theme.colors.OPACITY.DARK_KALE.LIGHTEST,
  },
  shareIcon: {
    width: 24,
    height: 24,
    borderRadius: theme.radius['3xLarge'],
    backgroundColor: theme.colors.OPACITY.DARK_KALE.LIGHTEST,
  },
});

// ─── Types ───────────────────────────────────────────────────────────────────

type Props = {
  itemsPerRow: number;
};

type FavoriteCardPlaceholderProps = {
  index: number;
};
